import { useRef } from 'react'
import type { GameweekPickerRow } from '../gameweekPicker'
import { CHIP_ICONS } from './chipIcons'
import './GameweekPicker.css'

interface GameweekPickerProps {
  rows: GameweekPickerRow[]
  /** Commit handler for a tapped row. The second arg is the row's name + points block — the source
   *  for the shared-element flight into the results modal. */
  onSelectGameweek: (gameweek: number, sourceAnchorEl: HTMLElement | null) => void
  /** Touch-down feedback: fires the light-impact haptic on press, before any commit. */
  onPressFeedback?: () => void
  /** True from the moment a row is tapped until the results modal is gone again. The dropdown fades
   *  out *while* the row lifts — the two must not animate against each other — and stays out of the
   *  way until the modal closes, at which point it fades back in, still open, so consecutive
   *  gameweeks can be checked without reopening the picker. */
  lifting?: boolean
}

function ChipIcons({ chips }: { chips: GameweekPickerRow['chips'] }) {
  if (chips.length === 0) return null
  return (
    <>
      {chips.map((chipId) => {
        const Icon = CHIP_ICONS[chipId]
        // Wrapper span rather than a bare glyph so the indicator can carry the fill + ink ring,
        // mirroring GameweekDetailHeading's badge — the label moves out to the
        // wrapper with it.
        return (
          <span
            key={chipId}
            className="gameweek-picker__chip-icon"
            aria-label={`${chipId} chip used`}
          >
            <Icon aria-hidden="true" />
          </span>
        )
      })}
    </>
  )
}

function PickerRow({
  row,
  onSelectGameweek,
  onPressFeedback,
}: {
  row: GameweekPickerRow
  onSelectGameweek: (gameweek: number, sourceAnchorEl: HTMLElement | null) => void
  onPressFeedback?: () => void
}) {
  const blockRef = useRef<HTMLSpanElement>(null)

  const block = (
    <span className="gameweek-picker__block" ref={blockRef}>
      <span className="gameweek-picker__name">
        Gameweek {row.gameweek}
        <ChipIcons chips={row.chips} />
      </span>
      {row.isLive ? (
        <span className="gameweek-picker__live">Live</span>
      ) : (
        <span className="gameweek-picker__points">{row.points} pts</span>
      )}
    </span>
  )

  // The live gameweek has no results to open, so it is not a control at all — rendering it as a
  // disabled button would still put it in the tab order and promise something it can't deliver.
  if (!row.selectable) {
    return (
      <div className="gameweek-picker__row gameweek-picker__row--live" aria-disabled="true">
        {block}
      </div>
    )
  }

  return (
    <button
      type="button"
      className="gameweek-picker__row"
      onPointerDown={onPressFeedback}
      onClick={() => onSelectGameweek(row.gameweek, blockRef.current)}
    >
      {block}
    </button>
  )
}

/** The Gameweek screen's title dropdown: one row per gameweek, name left and points
 * right, with the current week showing "Live". Tapping a past row expands its name + points block
 * into the results modal's heading via the shared-element flight. */
function GameweekPicker({
  rows,
  onSelectGameweek,
  onPressFeedback,
  lifting = false,
}: GameweekPickerProps) {
  return (
    <div
      id="gameweek-picker"
      className={`gameweek-picker${lifting ? ' gameweek-picker--lifting' : ''}`}
      aria-label="Gameweeks"
    >
      {rows.map((row) => (
        <PickerRow
          key={row.gameweek}
          row={row}
          onSelectGameweek={onSelectGameweek}
          onPressFeedback={onPressFeedback}
        />
      ))}
    </div>
  )
}

export default GameweekPicker
